import { change } from 'redux-form';
import { clearStore } from './app.actions';
import {
  ALLOWED_STATUSES,
  API_METHODS,
  LOCAL_STORAGE_KEYS,
} from 'config/constants';
import {
  writeToLocalState,
  getFromLocalState,
  clearLocalState,
} from 'utils/localStorage';
import { normalizeUserGroups } from 'utils/normalizeUserGroups';
import { clearAuth, saveAuth } from '@anova/dms/authSession';

const normalizePersonnelTypes = (personnelTypes) => {
  if (!Array.isArray(personnelTypes)) return personnelTypes;

  const mapOne = (t) => {
    if (t == null) return t;

    // Backend can serialize enums as numbers or strings.
    // Canonicalize to the backend enum *names* (pluralized), as used by QA automation.
    if (t === 1 || t === '1' || t === 'SponsorPersonnel')
      return 'SponsorPersonnel';
    if (
      t === 2 ||
      t === '2' ||
      t === 'ResearchSitePersonnel' ||
      t === 'ResearchSitesPersonnel'
    ) {
      return 'ResearchSitesPersonnel';
    }

    return t;
  };

  return personnelTypes.map(mapOne);
};

/** Decode JWT payload (client-side only; used to align authInfo with token claims). */
function parseJwtPayload(accessToken) {
  if (!accessToken || typeof accessToken !== 'string') {
    return null;
  }
  const parts = accessToken.split('.');
  if (parts.length < 2) {
    return null;
  }
  try {
    const base64url = parts[1];
    const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
    const padLen = (4 - (base64.length % 4)) % 4;
    const padded = base64 + (padLen ? '='.repeat(padLen) : '');
    return JSON.parse(globalThis.atob(padded));
  } catch {
    return null;
  }
}

/**
 * Fills authInfo from access token: numeric groups → names, org scope, personnel_types, sid → id.
 */
export function enrichAuthInfoFromToken(authInfo, accessToken) {
  if (!authInfo) {
    return authInfo;
  }

  let next = {
    ...authInfo,
    groups: normalizeUserGroups(authInfo.groups),
  };

  const pl = parseJwtPayload(accessToken);
  if (!pl) {
    return next;
  }

  next = { ...next };
  const groupsArr = Array.isArray(next.groups) ? next.groups : [];
  const isOrgPersonnelContext =
    groupsArr.includes('ResearchSitePersonnel') ||
    groupsArr.includes('SponsorPersonnel');
  const isPersonnelUser = groupsArr.includes('Personnel');
  const isPersonalPersonnelMode = isPersonnelUser && !isOrgPersonnelContext;

  // Personal personnel mode only — do not strip org scope for RS/Sponsor admins.
  if (isPersonalPersonnelMode) {
    next = {
      ...next,
      currentOrganizationId: null,
      sponsorId: null,
      researchSiteId: null,
    };
  }

  // Only infer org scope from JWT when the user is in org-personnel mode.
  // After PUT /personnels/own-account, org sub-roles are removed; stale JWT claims
  // must not force currentOrganizationId back and hide Linking Requests.
  if (
    isOrgPersonnelContext &&
    !next.currentOrganizationId &&
    pl.current_organization_id
  ) {
    next = { ...next, currentOrganizationId: pl.current_organization_id };
  }

  if (!next.currentOrganizationId && pl.sponsor_id) {
    if (groupsArr.includes('SponsorPersonnel')) {
      next = { ...next, currentOrganizationId: pl.sponsor_id };
    }
  }
  if (!next.currentOrganizationId && pl.research_site_id) {
    if (groupsArr.includes('ResearchSitePersonnel')) {
      next = { ...next, currentOrganizationId: pl.research_site_id };
    }
  }

  const sidClaim = 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/sid';
  const nameIdClaim =
    'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier';
  const sidFromJwt =
    pl[sidClaim] ?? pl.sid ?? pl.sub ?? pl[nameIdClaim] ?? pl.nameid;
  const missingId = next.id == null || next.id === '';
  if (missingId && sidFromJwt != null && sidFromJwt !== '') {
    next = { ...next, id: String(sidFromJwt) };
  }

  const hasPersonnelTypes =
    Array.isArray(next.personnelTypes) && next.personnelTypes.length > 0;
  if (!hasPersonnelTypes && pl.personnel_types) {
    try {
      const raw =
        typeof pl.personnel_types === 'string'
          ? JSON.parse(pl.personnel_types)
          : pl.personnel_types;
      if (Array.isArray(raw) && raw.length > 0) {
        next = {
          ...next,
          personnelTypes: normalizePersonnelTypes(raw),
        };
      }
    } catch {
      /* ignore malformed JWT fragment */
    }
  }

  return next;
}

const normalizeAuthPayload = (data) => {
  if (!data || typeof data !== 'object') return data;

  const authInfo = data.authInfo;
  if (!authInfo || typeof authInfo !== 'object') return data;

  const base = {
    ...authInfo,
    personnelTypes: normalizePersonnelTypes(authInfo.personnelTypes),
  };

  return {
    ...data,
    authInfo: enrichAuthInfoFromToken(base, data.accessToken),
  };
};

export const AUTH_ACTION_TYPES = {
  AUTH_LOGIN: 'AUTH_LOGIN',
  AUTH_LOGOUT: 'AUTH_LOGOUT',
  AUTH_UPDATE_TOKENS: 'AUTH_UPDATE_TOKENS',
  AUTH_UPDATE_AUTH_INFO: 'AUTH_UPDATE_AUTH_INFO',
  AUTH_UPDATE_MFA_TIMER: 'AUTH_UPDATE_MFA_TIMER',
  AUTH_CHANGE_PROCESSING: 'AUTH_CHANGE_PROCESSING',
  AUTH_STUDIES_COUNT: 'AUTH_STUDIES_COUNT',
  AUTH_CHECK_INVITE: 'AUTH_CHECK_INVITE',
  AUTH_CLEAR_RELOGIN_DATA: 'AUTH_CLEAR_RELOGIN_DATA',
};

export function login(body) {
  return async (dispatch, getState, { getAxios, getListAxios }) => {
    const axios = getAxios();

    clearAuth();

    dispatch(changeAuthProcessingState(true));

    const response = await axios.post(API_METHODS.AUTH_LOGIN, body);

    dispatch(changeAuthProcessingState(false));

    if (!response || response.code !== 'success') {
      return response;
    }

    const data = normalizeAuthPayload(response.data);

    saveAuth(data);

    dispatch({
      type: AUTH_ACTION_TYPES.AUTH_LOGIN,
      payload: {
        ...data,
      },
    });

    return response;
  };
}

export function resendMFAValidationCode() {
  return async (dispatch, getState, { getAxios, getListAxios }) => {
    const axios = getAxios();

    dispatch(changeAuthProcessingState(true));

    const response = await axios.post(
      API_METHODS.AUTH_MFA_RESEND_VALIDATION_CODE,
    );

    dispatch(changeAuthProcessingState(false));

    if (!response || response.code !== 'success') {
      return response;
    }

    const { data } = response;

    const currentUserState = getFromLocalState(LOCAL_STORAGE_KEYS.USER);
    const updatedUserState = {
      ...currentUserState,
      mfaCodeResendTime: data?.mfaCodeResendTime,
    };
    writeToLocalState(LOCAL_STORAGE_KEYS.USER, updatedUserState);

    dispatch(updateMfaTimer(data?.mfaCodeResendTime));

    return response;
  };
}

export function validateMFACode(payload) {
  return async (dispatch, getState, { getAxios, getListAxios }) => {
    const axios = getAxios();

    clearAuth();

    dispatch(changeAuthProcessingState(true));

    const response = await axios.post(
      API_METHODS.AUTH_MFA_VALIDATE_CODE,
      payload,
    );

    dispatch(changeAuthProcessingState(false));

    if (!response || response.code !== 'success') {
      return response;
    }

    const data = normalizeAuthPayload(response.data);

    saveAuth(data);

    dispatch({
      type: AUTH_ACTION_TYPES.AUTH_LOGIN,
      payload: {
        ...data,
      },
    });

    return response;
  };
}

export function signUp(body) {
  return async (dispatch, getState, { getAxios, getListAxios }) => {
    const axios = getAxios();

    clearAuth();

    dispatch(changeAuthProcessingState(true));

    const response = await axios.post(API_METHODS.REG_SHORT, body);

    dispatch(changeAuthProcessingState(false));

    if (!response || response.code !== 'success') {
      return response;
    }

    const data = normalizeAuthPayload(response.data);

    saveAuth(data);

    dispatch({
      type: AUTH_ACTION_TYPES.AUTH_LOGIN,
      payload: {
        ...data,
      },
    });

    return response;
  };
}

export function restoreUserStateFromLocalStorage() {
  const userState = normalizeAuthPayload(
    getFromLocalState(LOCAL_STORAGE_KEYS.USER),
  );
  const studiesCount = getFromLocalState(LOCAL_STORAGE_KEYS.STUDIES_COUNT);

  return {
    type: AUTH_ACTION_TYPES.AUTH_LOGIN,
    payload: {
      ...userState,
      studiesCount,
    },
  };
}

export function logout() {
  return async (dispatch, getState, { getAxios }) => {
    const axiosClient = getAxios();
    if (axiosClient && typeof axiosClient.clearErrorMessages === 'function') {
      axiosClient.clearErrorMessages();
    }

    clearAuth();
    clearLocalState(LOCAL_STORAGE_KEYS.STUDIES_COUNT);
    clearLocalState(LOCAL_STORAGE_KEYS.BREADCRUMBS_ARRAY);
    // Admin idle-logout timestamp, see packages/admin/src/shared/api/token.ts.
    localStorage.removeItem('admin_session_last_activity');

    dispatch({ type: AUTH_ACTION_TYPES.AUTH_LOGOUT });
    dispatch(clearStore());
  };
}

export function updateTokens(newTokensInfo) {
  const userState = getFromLocalState(LOCAL_STORAGE_KEYS.USER);
  const maybeAuthInfo = newTokensInfo?.authInfo;
  const accessToken =
    newTokensInfo?.accessToken ?? userState?.accessToken ?? undefined;

  const mergedBase = {
    ...userState?.authInfo,
    ...(maybeAuthInfo || {}),
    personnelTypes: normalizePersonnelTypes(
      maybeAuthInfo?.personnelTypes ?? userState?.authInfo?.personnelTypes,
    ),
  };

  if (maybeAuthInfo) {
    if (maybeAuthInfo.groups !== undefined) {
      mergedBase.groups = maybeAuthInfo.groups;
    }
    if (maybeAuthInfo.personnelTypes !== undefined) {
      mergedBase.personnelTypes = normalizePersonnelTypes(
        maybeAuthInfo.personnelTypes,
      );
    }
    if (maybeAuthInfo.currentOrganizationId !== undefined) {
      mergedBase.currentOrganizationId = maybeAuthInfo.currentOrganizationId;
    }
  }

  const enrichedAuthInfo =
    userState?.authInfo != null || maybeAuthInfo != null
      ? enrichAuthInfoFromToken(mergedBase, accessToken)
      : undefined;

  const newUserState = {
    ...userState,
    ...newTokensInfo,
    ...(enrichedAuthInfo != null ? { authInfo: enrichedAuthInfo } : {}),
  };
  saveAuth(newUserState);
  return {
    type: AUTH_ACTION_TYPES.AUTH_UPDATE_TOKENS,
    payload: {
      ...newTokensInfo,
      ...(enrichedAuthInfo != null ? { authInfo: enrichedAuthInfo } : {}),
    },
  };
}

export function getNewAuthInfo() {
  return async (dispatch, getState, { getAxios, getListAxios }) => {
    const axios = getAxios();

    const response = await axios.get(`${API_METHODS.USER}`);

    if (!response || response.code !== 'success') {
      return response;
    }

    const { data } = response;
    const previousStatus = getState()?.authState?.authInfo?.status;
    if (
      previousStatus === ALLOWED_STATUSES.PENDING_VERIFICATION_STATUS &&
      data?.status === ALLOWED_STATUSES.GRANTED_VERIFICATION_STATUS
    ) {
      await dispatch(logout());
      return response;
    }

    dispatch(rewriteAuthInfo(data));

    return response;
  };
}

export function rewriteAuthInfo(newAuthInfo) {
  const userState = getFromLocalState(LOCAL_STORAGE_KEYS.USER);
  const token = userState?.accessToken;
  const merged = {
    ...userState?.authInfo,
    ...newAuthInfo,
    personnelTypes: normalizePersonnelTypes(
      newAuthInfo?.personnelTypes ?? userState?.authInfo?.personnelTypes,
    ),
  };
  const normalizedAuthInfo = enrichAuthInfoFromToken(merged, token);
  const newUserState = {
    ...userState,
    authInfo: {
      ...normalizedAuthInfo,
    },
  };
  writeToLocalState(LOCAL_STORAGE_KEYS.USER, newUserState);
  return {
    type: AUTH_ACTION_TYPES.AUTH_UPDATE_AUTH_INFO,
    payload: normalizedAuthInfo,
  };
}

export function changeAuthProcessingState(newState) {
  return {
    type: AUTH_ACTION_TYPES.AUTH_CHANGE_PROCESSING,
    payload: newState,
  };
}

export function updateMfaTimer(mfaCodeResendTime) {
  return {
    type: AUTH_ACTION_TYPES.AUTH_UPDATE_MFA_TIMER,
    payload: { mfaCodeResendTime },
  };
}

export function checkInviteId(token) {
  return async (dispatch, getState, { getAxios, getListAxios }) => {
    const axios = getAxios();
    const response = await axios.get(`${API_METHODS.INACTIVE_INVITE(token)}`);

    if (!response || response.code !== 'success') {
      dispatch(rewriteAuthInfo({ inviteData: {} }));

      return response;
    }

    const { data } = response;

    dispatch(rewriteAuthInfo({ inviteData: data }));
    dispatch(change('signUpForm', `email`, data.email));
    dispatch(change('signUpForm', `firstName`, data.firstName));
    dispatch(change('signUpForm', `lastName`, data.lastName));
    dispatch(change('signUpForm', `invitedUserId`, data.id));

    return response;
  };
}

export function clearInviteData() {
  return async (dispatch, getState, { getAxios, getListAxios }) => {
    dispatch(rewriteAuthInfo({ inviteData: {} }));
    dispatch(change('signUpForm', `email`, ''));
    dispatch(change('signUpForm', `invitedUserId`, ''));
  };
}

export function clearReLoginData() {
  return {
    type: AUTH_ACTION_TYPES.AUTH_CLEAR_RELOGIN_DATA,
  };
}
